import { z } from 'zod'
import { err, ok } from '../utils/result.ts'
import type { AIModel } from '../types/enums.ts'
import type { Env } from '../types/env.ts'
import { BRAND_VISIBILITY_TOOL, TOOL_NAME } from './tool-schema.ts'
import { buildBatchPrompt } from './prompt.ts'
import type { AIIntegration, BatchMentionResult, BatchResponse } from './types.ts'
import { SENTIMENTS } from '../types/enums.ts'

const MODEL_IDS: Record<'claude-haiku' | 'claude-sonnet' | 'claude-opus', string> = {
  'claude-haiku':  'claude-haiku-4-5-20251001',
  'claude-sonnet': 'claude-sonnet-4-6',
  'claude-opus':   'claude-opus-4-7',
}

const ToolUseBlockSchema = z.object({
  type: z.literal('tool_use'),
  name: z.literal(TOOL_NAME),
  input: z.object({
    results: z.array(z.object({
      prompt_id:           z.string(),
      mentioned:           z.boolean(),
      rank_position:       z.number().int().nullable().optional(),
      sentiment:           z.enum(SENTIMENTS).nullable().optional(),
      context_snippet:     z.string().nullable().optional(),
      competitor_mentions: z.array(z.string()).optional(),
      ai_answer:           z.string().optional(),
    })),
  }),
})

const AnthropicResponseSchema = z.object({
  content: z.array(z.union([
    ToolUseBlockSchema,
    z.object({ type: z.string() }).passthrough(),
  ])).min(1),
  usage: z.object({
    input_tokens: z.number().nullable().optional(),
    output_tokens: z.number().nullable().optional(),
  }).optional(),
})

type AnthropicVariant = 'claude-haiku' | 'claude-sonnet' | 'claude-opus'

export function createAnthropicIntegration(variant: AnthropicVariant): AIIntegration {
  return {
    model: variant as AIModel,

    async queryBatch(params, env: Env) {
      const prompt = buildBatchPrompt(params)
      let res: Response
      try {
        res = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'x-api-key': env.ANTHROPIC_KEY,
            'anthropic-version': '2023-06-01',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: MODEL_IDS[variant],
            max_tokens: 4096,
            tools: [BRAND_VISIBILITY_TOOL],
            tool_choice: { type: 'tool', name: TOOL_NAME },
            messages: [{ role: 'user', content: prompt }],
          }),
        })
      } catch (e) {
        return err(`Anthropic network error: ${e instanceof Error ? e.message : String(e)}`)
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        return err(`Anthropic API error ${res.status}: ${text}`)
      }

      let data: unknown
      try { data = await res.json() } catch {
        return err('Anthropic returned invalid JSON')
      }

      const parsed = AnthropicResponseSchema.safeParse(data)
      if (!parsed.success) return err(`Anthropic response parse error: ${parsed.error.message}`)

      const toolBlock = parsed.data.content.find(
        (b): b is z.infer<typeof ToolUseBlockSchema> => b.type === 'tool_use',
      )
      if (!toolBlock) return err('Anthropic did not return tool_use block')

      const promptMap = new Map(params.prompts.map((p) => [p.id, p.promptText]))

      const results: BatchMentionResult[] = toolBlock.input.results.map((r) => ({
        keywordId: r.prompt_id,
        promptText: promptMap.get(r.prompt_id) ?? '',
        mentioned: r.mentioned,
        rankPosition: r.rank_position ?? null,
        sentiment: r.sentiment ?? null,
        contextSnippet: r.context_snippet ?? null,
        competitorMentions: r.competitor_mentions ?? [],
        aiAnswer: r.ai_answer ?? null,
      }))

      const response: BatchResponse = {
        results,
        usage: {
          inputTokens: parsed.data.usage?.input_tokens ?? null,
          outputTokens: parsed.data.usage?.output_tokens ?? null,
        },
      }
      return ok(response)
    },
  }
}
